import type { PinOnImageStaticContent } from '@core-api/presentation/types/response';
import { getVisualizationState } from '../../utils/getVisualizationState';
import {
  usePresentationContext,
  usePresentationSlideContext,
} from '../../legacy-context/presentationContext';
import { useScreenshotReady } from '../../shared/hooks/use-screenshot-ready';
import { PinOnImagePresentation } from './PinOnImagePresentation';
import { usePinOnImageResults } from './hooks/use-pin-on-image-results';
import { usePinOnImagePreviewResults } from './hooks/use-pin-on-image-preview-results';

// Needs to be a type instead of interface as the interface can't satisfy the generic used with useSlideState hook
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type SlideStatePinOnImage = { reveal?: boolean };

export const PinOnImagePresentationDataHandler = () => {
  const {
    InteractiveCanvasComponents,
    isPreviewResultsEnabled,
    useConfig,
    useCurrentSession,
    usePace,
    useSlideState,
  } = usePresentationContext();

  const [slideState] = useSlideState<SlideStatePinOnImage>();
  const { enableAnimations, mode } = useConfig();
  const { reveal } = slideState || {};
  const { currentStep } = usePace();
  const slide = usePresentationSlideContext();
  const { isCurrentSessionActive, activeSessionId } = useCurrentSession();
  const staticContent = slide.staticContent as PinOnImageStaticContent;
  const interactiveContent = slide.interactiveContents[0]!;

  const visualizationState = getVisualizationState({
    mode,
    currentStep,
  });

  const shouldHideResults =
    slide.responseVisibility === 'hidden' ||
    visualizationState === 'hide-answers';

  const { results, isLoading } = usePinOnImageResults({
    adminKey: slide.slideAdminKey,
    interactiveContentId: interactiveContent.interactiveContentId,
    shouldFetchResults: !shouldHideResults && !isPreviewResultsEnabled,
    sessionId: isCurrentSessionActive ? undefined : activeSessionId,
  });

  const previewResults = usePinOnImagePreviewResults({
    isEnabled: isPreviewResultsEnabled,
    correctArea: interactiveContent.correctArea,
  });

  useScreenshotReady({ isReady: mode === 'screenshot' && !isLoading });

  let showCorrectAnswerState: 'disabled' | 'enabled-visible' | 'enabled-hidden';

  if (interactiveContent.correctAnswerMode === 'disabled') {
    showCorrectAnswerState = 'disabled';
  } else if (
    reveal ||
    ['screenshot', 'public'].includes(mode) ||
    visualizationState === 'view-correct-answer'
  ) {
    showCorrectAnswerState = 'enabled-visible';
  } else {
    showCorrectAnswerState = 'enabled-hidden';
  }

  const imageUrl = interactiveContent.image?.presets?.original.url ?? undefined;

  return (
    <PinOnImagePresentation
      title={slide.title}
      styledTitle={staticContent.styledTitle as Record<string, unknown>}
      label={staticContent.label}
      longerDescription={interactiveContent.description}
      fontSizeOffset={staticContent.textSizeOffset}
      CanvasVisualizationArea={
        InteractiveCanvasComponents?.CanvasVisualizationArea
      }
      results={isPreviewResultsEnabled ? previewResults : results}
      imageUrl={imageUrl}
      showCorrectAnswerState={showCorrectAnswerState}
      correctArea={interactiveContent.correctArea}
      shouldAnimate={enableAnimations}
      shouldHideResults={shouldHideResults}
    />
  );
};
